import {
  useState,
  useContext,
  useMemo,
  useEffect,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Collapse from "@mui/material/Collapse";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { SelectedMeeting } from "../../../pages/notes/meetingNotes";
import EditableMeetingInfo from "../EditableMeetingInfo";
import { Meeting, MeetingStatus } from "../../../models/meeting";
import {
  MeetingItem,
  MeetingItemStatus,
  MeetingItemType,
  RiskIndicator,
} from "../../../models/meetingItem";
import {
  PersonalNote,
  PersonalNoteTargetType,
} from "../../../models/personalNote";
import { Context } from "../../context";
import { AppContext } from "../../../models/applicationState";
import * as itemActions from "../../../actions/itemActions";
import { ItemActions } from "../../../actions/itemActions";
import { bindActionCreators } from "../../../actions/actionCreators";
import MeetingNotesItemGrid from "../MeetingNotesItemGrid";
import { ApiUser, UserRole } from "../../../models";
import { bulkCreateUsers } from "../../../services/userService";
import * as userActions from "../../../actions/userActions";
import config from "../../../config";
import { createPersonalNoteService } from "../../../services/personalNoteService";
import { FreeSingleSelect } from "../../freeSingleSelect";
import {
  AttachmentsModal,
  type PendingAttachment,
} from "../../attachmentsModal";
import ConfirmationPanel from "../../confirmationPanel";
import Badge from "@mui/material/Badge";
import Tooltip from "@mui/material/Tooltip";
import {
  createBlobInContainer,
  createAttachment,
  decorateBlobs,
} from "../../../services/attachmentService";

interface TakeNotesStepProps {
  onNext: () => void;
  onBack: () => void;
  items: MeetingItem[];
  onItemsChange: (items: MeetingItem[]) => void;
  selectedMeeting: SelectedMeeting | null;
  onMeetingChange: (meeting: SelectedMeeting) => void;
  onMeetingStatusChange: (
    status: MeetingStatus,
    updates?: Partial<Meeting>,
  ) => Promise<boolean>;
  personalNotes: PersonalNote[];
  onPersonalNotesChange: (notes: PersonalNote[]) => void;
  onError: (message: string, error?: unknown) => void;
}

const typeOptions = [
  {
    value: MeetingItemType.Task,
    labelKey: "meetingNotes.common.itemTypes.task",
    icon: <TaskAltOutlinedIcon sx={{ fontSize: 16 }} />,
  },
  {
    value: MeetingItemType.Decision,
    labelKey: "meetingNotes.common.itemTypes.decision",
    icon: <GavelOutlinedIcon sx={{ fontSize: 16 }} />,
  },
  {
    value: MeetingItemType.Info,
    labelKey: "meetingNotes.common.itemTypes.info",
    icon: <InfoOutlinedIcon sx={{ fontSize: 16 }} />,
  },
];

const getItemKey = (item: MeetingItem, index: number) =>
  item.id || `meeting-item-${index}`;

const isLocalOnlyItemId = (itemId?: string) =>
  !itemId || itemId.startsWith("item-") || itemId.startsWith("meeting-item-");

const isValidEmail = (email: string) => {
  const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailPattern.test(email);
};

const createUserIfNeeded = async (email: string): Promise<boolean> => {
  try {
    const trimmedEmail = email.trim();
    const users: ApiUser[] = [{ email: trimmedEmail, role: UserRole.Actor }];
    await bulkCreateUsers(users);
    return true;
  } catch (error) {
    console.warn("User creation failed or user already exists:", error);
    return true;
  }
};

export interface TakeNotesStepHandle {
  guardNavigation: (proceed: () => void | Promise<void>) => void;
}

const TakeNotesStep = forwardRef<TakeNotesStepHandle, TakeNotesStepProps>(
  (
    {
      onBack,
      items,
      onItemsChange,
      selectedMeeting,
      onMeetingChange,
      onMeetingStatusChange,
      personalNotes,
      onPersonalNotesChange,
      onError,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const { state, dispatch } = useContext(Context) as AppContext;
    const [editingItem, setEditingItem] = useState<MeetingItem | null>(null);
    const [itemCategory, setItemCategory] = useState("");
    const [itemDescription, setItemDescription] = useState("");
    const [itemResponsible, setItemResponsible] = useState("");
    const [itemDueDate, setItemDueDate] = useState<Dayjs | null>(null);
    const [newItemType, setNewItemType] = useState<string>("task");
    const [draftPersonalNote, setDraftPersonalNote] = useState("");
    const [pendingPersonalNoteDrafts, setPendingPersonalNoteDrafts] = useState<
      string[]
    >([]);
    const [personalNotesOpen, setPersonalNotesOpen] = useState(false);
    const [attachmentsModalOpen, setAttachmentsModalOpen] = useState(false);
    const [pendingAttachmentsCount, setPendingAttachmentsCount] = useState(0);
    const [pendingAttachments, setPendingAttachments] = useState<
      PendingAttachment[]
    >([]);
    const composerRef = useRef<HTMLDivElement | null>(null);
    const preservePersonalNotesDraftsRef = useRef(false);
    const preserveAttachmentsRef = useRef(false);

    const actions = useMemo(
      () => bindActionCreators(itemActions, dispatch) as unknown as ItemActions,
      [dispatch],
    );
    const personalNoteService = useMemo(
      () => createPersonalNoteService(config.api.baseUrl),
      [],
    );

    const [pendingAction, setPendingAction] = useState<
      null | (() => void | Promise<void>)
    >(null);

    const isDirty = useMemo(() => {
      if (
        draftPersonalNote.trim() ||
        pendingPersonalNoteDrafts.length > 0 ||
        pendingAttachments.length > 0
      ) {
        return true;
      }

      if (!editingItem) {
        return !!(
          itemCategory.trim() ||
          itemDescription.trim() ||
          itemResponsible.trim() ||
          itemDueDate ||
          newItemType !== MeetingItemType.Task
        );
      }

      const baselineDescription =
        editingItem.description || editingItem.content || "";
      const baselineCategory = editingItem.category || "";
      const baselineResponsible = editingItem.actor || "";
      const baselineType = editingItem.types?.[0] || MeetingItemType.Task;
      const currentDueMs = itemDueDate ? itemDueDate.valueOf() : null;
      const baselineDueMs = editingItem.dueDate
        ? dayjs(editingItem.dueDate).valueOf()
        : null;

      return (
        itemCategory.trim() !== baselineCategory.trim() ||
        itemDescription.trim() !== baselineDescription.trim() ||
        itemResponsible.trim() !== baselineResponsible.trim() ||
        newItemType !== baselineType ||
        currentDueMs !== baselineDueMs
      );
    }, [
      editingItem,
      itemCategory,
      itemDescription,
      itemResponsible,
      itemDueDate,
      newItemType,
      draftPersonalNote,
      pendingPersonalNoteDrafts,
      pendingAttachments,
    ]);

    const guardDirty = useCallback(
      (action: () => void | Promise<void>) => {
        if (isDirty) {
          setPendingAction(() => action);
        } else {
          void action();
        }
      },
      [isDirty],
    );

    const confirmDiscard = () => {
      const action = pendingAction;
      setPendingAction(null);
      void action?.();
    };

    const cancelDiscard = () => setPendingAction(null);

    useImperativeHandle(
      ref,
      () => ({
        guardNavigation: (proceed) => guardDirty(proceed),
      }),
      [guardDirty],
    );

    const resetComposer = useCallback(() => {
      preservePersonalNotesDraftsRef.current = false;
      preserveAttachmentsRef.current = false;
      setEditingItem(null);
      setItemCategory("");
      setItemDescription("");
      setItemResponsible("");
      setItemDueDate(null);
      setNewItemType(MeetingItemType.Task);
      setDraftPersonalNote("");
      setPendingPersonalNoteDrafts([]);
      setPersonalNotesOpen(false);
      setAttachmentsModalOpen(false);
      setPendingAttachmentsCount(0);
      setPendingAttachments([]);
    }, []);

    const ensureMeetingDraftStatus = useCallback(async () => {
      if (selectedMeeting?.originalMeeting?.status === MeetingStatus.Draft) {
        return true;
      }

      return onMeetingStatusChange(MeetingStatus.Draft);
    }, [onMeetingStatusChange, selectedMeeting?.originalMeeting?.status]);

    useEffect(() => {
      if (!editingItem) {
        return;
      }

      setItemCategory(editingItem.category || "");
      setItemDescription(editingItem.description || editingItem.content || "");
      setItemResponsible(editingItem.actor || "");
      setItemDueDate(editingItem.dueDate ? dayjs(editingItem.dueDate) : null);
      setNewItemType(editingItem.types?.[0] || MeetingItemType.Task);
      if (preservePersonalNotesDraftsRef.current) {
        preservePersonalNotesDraftsRef.current = false;
        return;
      }

      setDraftPersonalNote("");
      setPendingPersonalNoteDrafts([]);

      if (!preserveAttachmentsRef.current) {
        setPendingAttachmentsCount(0);
        setPendingAttachments([]);
      }
      preserveAttachmentsRef.current = false;
    }, [editingItem]);

    useEffect(() => {
      if (newItemType !== MeetingItemType.Task) {
        setItemResponsible("");
        setItemDueDate(null);
      }
    }, [newItemType]);

    const queueDraftPersonalNote = () => {
      const trimmedDraft = draftPersonalNote.trim();
      if (!trimmedDraft) {
        return;
      }

      setPendingPersonalNoteDrafts((currentDrafts) => [
        ...currentDrafts,
        trimmedDraft,
      ]);
      setDraftPersonalNote("");
      setPersonalNotesOpen(true);
    };

    const removeQueuedPersonalNote = (indexToRemove: number) => {
      setPendingPersonalNoteDrafts((currentDrafts) =>
        currentDrafts.filter((_, index) => index !== indexToRemove),
      );
    };

    const handleAttachmentsCountChanged = useCallback((count: number) => {
      setPendingAttachmentsCount(count);
    }, []);

    const handleAttachmentsModalClose = useCallback(async () => {
      setAttachmentsModalOpen(false);
    }, []);

    const handlePendingAttachmentsChange = useCallback(
      (newPendingAttachments: PendingAttachment[]) => {
        setPendingAttachments(newPendingAttachments);
        setPendingAttachmentsCount(newPendingAttachments.length);
      },
      [],
    );

    const getPendingPersonalNoteContents = () => {
      const trimmedDraft = draftPersonalNote.trim();
      return trimmedDraft
        ? [...pendingPersonalNoteDrafts, trimmedDraft]
        : [...pendingPersonalNoteDrafts];
    };

    const savePersonalNotesForItem = async (
      itemId: string,
      noteContents: string[],
    ): Promise<{
      savedNotes: PersonalNote[];
      failedContents: string[];
      firstError?: unknown;
    }> => {
      if (!itemId || noteContents.length === 0) {
        return { savedNotes: [], failedContents: [] };
      }

      const settledResults = await Promise.allSettled(
        noteContents.map((content) =>
          personalNoteService.createNote({
            targetType: PersonalNoteTargetType.MeetingItem,
            targetId: itemId,
            content,
          }),
        ),
      );

      const savedNotes: PersonalNote[] = [];
      const failedContents: string[] = [];
      let firstError: unknown;

      settledResults.forEach((result, index) => {
        if (result.status === "fulfilled") {
          savedNotes.push(result.value);
          return;
        }

        failedContents.push(noteContents[index]);
        if (firstError === undefined) {
          firstError = result.reason;
        }
      });

      return { savedNotes, failedContents, firstError };
    };

    const savePendingAttachmentsForItem = async (
      itemId: string,
      meetingId: string,
      attachmentsToSave: PendingAttachment[],
    ): Promise<{
      savedCount: number;
      failedAttachments: PendingAttachment[];
      firstError?: unknown;
    }> => {
      if (!itemId || !meetingId || attachmentsToSave.length === 0) {
        return { savedCount: 0, failedAttachments: [] };
      }

      const settledResults = await Promise.allSettled(
        attachmentsToSave.map(async (pending) => {
          if (pending.file) {
            // Upload file to blob storage and create attachment record
            const blob = await createBlobInContainer(
              `items/meetings/${meetingId}/items/${itemId}/`,
              pending.file,
              meetingId,
              itemId,
            );
            const savedAttachment = await createAttachment(
              blob,
              meetingId,
              itemId,
            );
            // Decorate with SAS token
            const [decorated] = await decorateBlobs([
              {
                ...blob,
                ...savedAttachment,
                displayName: savedAttachment.displayName || blob.displayName,
              },
            ]);
            return decorated;
          } else {
            // Hyperlink - just create the attachment record
            const savedAttachment = await createAttachment(
              pending.attachment,
              meetingId,
              itemId,
            );
            return savedAttachment;
          }
        }),
      );

      let savedCount = 0;
      const failedAttachments: PendingAttachment[] = [];
      let firstError: unknown;

      settledResults.forEach((result, index) => {
        if (result.status === "fulfilled") {
          savedCount++;
          // Revoke object URL for uploaded files
          const pending = attachmentsToSave[index];
          if (pending.file && pending.attachment.url.startsWith("blob:")) {
            URL.revokeObjectURL(pending.attachment.url);
          }
          return;
        }

        failedAttachments.push(attachmentsToSave[index]);
        if (firstError === undefined) {
          firstError = result.reason;
        }
      });

      return { savedCount, failedAttachments, firstError };
    };

    const persistComposerItem = async (createAsNew = false): Promise<void> => {
      const sourceEditingItem = createAsNew ? null : editingItem;
      const pendingNoteContents = getPendingPersonalNoteContents();
      // Capture pending attachments for new items (when sourceEditingItem has no id)
      const attachmentsToSave = !sourceEditingItem?.id
        ? [...pendingAttachments]
        : [];
      const newItem: MeetingItem = {
        ...(sourceEditingItem || {}),
        content: itemDescription.trim(),
        description: itemDescription.trim(),
        category: itemCategory.trim() || undefined,
        actor:
          newItemType === MeetingItemType.Task
            ? itemResponsible.trim() || undefined
            : "",
        dueDate: itemDueDate ? itemDueDate.toDate() : undefined,
        types: [newItemType],
        status: sourceEditingItem?.status || MeetingItemStatus.NotStarted,
        risk: sourceEditingItem?.risk || RiskIndicator.NoRisk,
        meetingId: selectedMeeting?.id,
        createdDate: sourceEditingItem?.createdDate || new Date(),
      };

      const responsibleTrimmed = itemResponsible.trim();
      const categoryTrimmed = itemCategory.trim();
      const shouldUpdateEmailSuggestion =
        newItemType === MeetingItemType.Task &&
        !!responsibleTrimmed &&
        isValidEmail(responsibleTrimmed);
      const shouldUpdateCategorySuggestion = !!categoryTrimmed;

      if (shouldUpdateEmailSuggestion || shouldUpdateCategorySuggestion) {
        (async () => {
          if (shouldUpdateEmailSuggestion) {
            await createUserIfNeeded(responsibleTrimmed);
          }
          if (!state.invoker) return;
          const invoker = state.invoker;
          const upsert = (
            arr: Array<{ value: string; count: number }>,
            val: string,
          ) => {
            const v = val.toString();
            const idx = arr.findIndex((e) => e.value === v);
            if (idx >= 0) {
              const next = [...arr];
              next[idx] = { value: v, count: (next[idx]?.count ?? 0) + 1 };
              return next;
            }
            return [...arr, { value: v, count: 1 }];
          };
          const nextInvoker = { ...invoker };
          if (shouldUpdateEmailSuggestion) {
            const currentEmailSuggestions = (invoker.emailSuggestions ||
              []) as Array<{ value: string; count: number }>;
            nextInvoker.emailSuggestions = upsert(
              currentEmailSuggestions,
              responsibleTrimmed,
            );
          }
          if (shouldUpdateCategorySuggestion) {
            const currentCategorySuggestions = (invoker.categorySuggestions ||
              []) as Array<{ value: string; count: number }>;
            nextInvoker.categorySuggestions = upsert(
              currentCategorySuggestions,
              categoryTrimmed,
            );
          }
          dispatch(userActions.setInvokerAction(nextInvoker));
          try {
            await userActions.save(nextInvoker);
            await userActions.getInvoker()(dispatch);
          } catch (e) {
            console.warn("Failed to update suggestions:", e);
          }
        })();
      }

      if (selectedMeeting?.id) {
        try {
          const savedItem = await actions.save(selectedMeeting.id, newItem);
          const isAddingNewItem = !sourceEditingItem?.id;
          onItemsChange(
            sourceEditingItem?.id
              ? items.map((item) =>
                  item.id === sourceEditingItem.id ? savedItem : item,
                )
              : [...items, savedItem],
          );

          // Only set to Draft when adding a new item, not when editing
          if (isAddingNewItem) {
            await ensureMeetingDraftStatus();
          }

          // Track errors from notes and attachments
          let hasErrors = false;
          let errorMessage = "";
          let firstError: unknown;

          // Save pending personal notes
          if (savedItem.id && pendingNoteContents.length > 0) {
            const noteSaveResult = await savePersonalNotesForItem(
              savedItem.id,
              pendingNoteContents,
            );

            if (noteSaveResult.savedNotes.length > 0) {
              onPersonalNotesChange([
                ...(Array.isArray(personalNotes) ? personalNotes : []),
                ...noteSaveResult.savedNotes,
              ]);
            }

            if (noteSaveResult.failedContents.length > 0) {
              hasErrors = true;
              preservePersonalNotesDraftsRef.current = true;
              setDraftPersonalNote("");
              setPendingPersonalNoteDrafts(noteSaveResult.failedContents);
              setPersonalNotesOpen(true);
              errorMessage = t(
                "meetingNotes.takeNotes.errors.partialSave.personalNotes",
                {
                  count: noteSaveResult.failedContents.length,
                },
              );
              firstError = noteSaveResult.firstError;
            }
          }

          // Save pending attachments for new items
          if (savedItem.id && attachmentsToSave.length > 0) {
            const attachmentSaveResult = await savePendingAttachmentsForItem(
              savedItem.id,
              selectedMeeting.id,
              attachmentsToSave,
            );

            if (attachmentSaveResult.failedAttachments.length > 0) {
              hasErrors = true;
              preserveAttachmentsRef.current = true;
              setPendingAttachments(attachmentSaveResult.failedAttachments);
              const attachmentErrorMsg = t(
                "meetingNotes.takeNotes.errors.partialSave.attachments",
                {
                  count: attachmentSaveResult.failedAttachments.length,
                },
              );
              errorMessage = errorMessage
                ? `${errorMessage}${t("meetingNotes.takeNotes.errors.partialSave.join")}${attachmentErrorMsg}`
                : attachmentErrorMsg;
              if (!firstError) {
                firstError = attachmentSaveResult.firstError;
              }
            }
          }

          if (hasErrors) {
            preserveAttachmentsRef.current = true;
            setEditingItem(savedItem);
            onError(
              t("meetingNotes.takeNotes.errors.partialSave.summary", {
                details: errorMessage,
              }),
              firstError,
            );
            return;
          }

          resetComposer();
          return;
        } catch (error) {
          onError(
            sourceEditingItem
              ? t("meetingNotes.takeNotes.errors.updateItem")
              : createAsNew
                ? t("meetingNotes.takeNotes.errors.createNewItem")
                : t("meetingNotes.takeNotes.errors.addItem"),
            error,
          );
        }
      }

      const fallbackItem = {
        ...newItem,
        id: sourceEditingItem?.id || `item-${Date.now()}`,
      };

      const isAddingNewItem = !sourceEditingItem?.id;
      onItemsChange(
        sourceEditingItem?.id
          ? items.map((item) =>
              item.id === sourceEditingItem.id ? fallbackItem : item,
            )
          : [...items, fallbackItem],
      );

      // Only set to Draft when adding a new item, not when editing
      if (isAddingNewItem) {
        await ensureMeetingDraftStatus();
      }

      // Handle pending notes and attachments for local-only items
      const hasPendingNotes = pendingNoteContents.length > 0;
      const hasPendingAttachmentsLocal = attachmentsToSave.length > 0;

      if (hasPendingNotes || hasPendingAttachmentsLocal) {
        if (hasPendingNotes) {
          preservePersonalNotesDraftsRef.current = true;
          setDraftPersonalNote("");
          setPendingPersonalNoteDrafts(pendingNoteContents);
          setPersonalNotesOpen(true);
        }
        if (hasPendingAttachmentsLocal) {
          preserveAttachmentsRef.current = true;
          // Keep pending attachments as-is for retry
        }
        setEditingItem(fallbackItem);

        const errorParts: string[] = [];
        if (hasPendingNotes) {
          errorParts.push(
            t("meetingNotes.takeNotes.errors.localPending.personalNotes"),
          );
        }
        if (hasPendingAttachmentsLocal) {
          errorParts.push(
            t("meetingNotes.takeNotes.errors.localPending.attachments"),
          );
        }
        onError(
          t("meetingNotes.takeNotes.errors.localPending.summary", {
            details: errorParts.join(
              t("meetingNotes.takeNotes.errors.localPending.join"),
            ),
          }),
        );
        return;
      }
      resetComposer();
    };

    const handleSaveItem = async () => {
      if (!itemDescription.trim()) {
        return;
      }

      await persistComposerItem(false);
    };

    const handleDeleteItem = useCallback(
      async (itemId: string) => {
        const itemToDelete = items.find(
          (item, index) => getItemKey(item, index) === itemId,
        );
        if (
          selectedMeeting?.id &&
          itemToDelete?.id &&
          !isLocalOnlyItemId(itemToDelete.id)
        ) {
          try {
            await actions.remove(selectedMeeting.id, itemToDelete);
          } catch (error) {
            onError(t("meetingNotes.takeNotes.errors.deleteItem"), error);
          }
        }

        onItemsChange(
          items.filter((item, index) => getItemKey(item, index) !== itemId),
        );
        if (
          editingItem === itemToDelete ||
          editingItem?.id === itemToDelete?.id
        ) {
          resetComposer();
        }
      },
      [
        actions,
        editingItem,
        items,
        onError,
        onItemsChange,
        resetComposer,
        selectedMeeting?.id,
      ],
    );

    const handleUpdateItem = useCallback(
      async (updatedItem: MeetingItem) => {
        const itemId = updatedItem.id;

        if (
          selectedMeeting?.id &&
          updatedItem.id &&
          !isLocalOnlyItemId(updatedItem.id)
        ) {
          try {
            const savedItem = await actions.save(
              selectedMeeting.id,
              updatedItem,
            );
            onItemsChange(
              items.map((item, index) =>
                getItemKey(item, index) === updatedItem.id ? savedItem : item,
              ),
            );
            return true;
          } catch (error) {
            onError(t("meetingNotes.takeNotes.errors.updateItem"), error);
            return false;
          }
        }

        if (!itemId) {
          return false;
        }

        onItemsChange(
          items.map((item, index) =>
            getItemKey(item, index) === itemId
              ? { ...item, ...updatedItem }
              : item,
          ),
        );
        return true;
      },
      [actions, items, onError, onItemsChange, selectedMeeting?.id],
    );

    const handleUpdateAttachmentsCount = useCallback(
      (itemId: string, count: number) => {
        onItemsChange(
          items.map((item, index) =>
            getItemKey(item, index) === itemId
              ? { ...item, attachments: count }
              : item,
          ),
        );
      },
      [items, onItemsChange],
    );

    const handleSelectItem = useCallback(
      (item: MeetingItem) => {
        const isSameItem =
          !!editingItem &&
          (item === editingItem ||
            (!!editingItem.id && item.id === editingItem.id));
        const performSelect = () => {
          setEditingItem(item);
          composerRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        };
        if (isSameItem) {
          performSelect();
          return;
        }
        guardDirty(performSelect);
      },
      [editingItem, guardDirty],
    );

    const handleCreateNewItem = async () => {
      if (!itemDescription.trim()) {
        return;
      }

      await persistComposerItem(true);
      composerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    };

    const selectedItemIndex = editingItem
      ? items.findIndex(
          (item) =>
            item === editingItem ||
            (!!editingItem.id && item.id === editingItem.id),
        )
      : -1;
    const selectedItemKey =
      editingItem && selectedItemIndex >= 0
        ? getItemKey(items[selectedItemIndex], selectedItemIndex)
        : null;
    const hasPendingPersonalNotes =
      pendingPersonalNoteDrafts.length > 0 || !!draftPersonalNote.trim();
    const saveButtonLabel = editingItem
      ? hasPendingPersonalNotes
        ? t("meetingNotes.takeNotes.actions.updateItemAndNotes")
        : t("meetingNotes.takeNotes.actions.updateItem")
      : hasPendingPersonalNotes
        ? t("meetingNotes.takeNotes.actions.addItemAndNotes")
        : t("meetingNotes.takeNotes.actions.addItem");
    const personalNotesHelperText = editingItem
      ? t("meetingNotes.takeNotes.personalNotes.helperWhenEditing")
      : t("meetingNotes.takeNotes.personalNotes.helperWhenCreating");

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          mx: -3,
          mt: -4,
        }}
      >
        {/* Meeting info header — editable */}
        {selectedMeeting && (
          <Box
            sx={{
              px: 3,
              py: 1,
              backgroundColor: "#F8F9FC",
              borderBottom: "1px solid #E5E7EB",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <EditableMeetingInfo
              meeting={selectedMeeting}
              onMeetingChange={onMeetingChange}
              layout="compact"
            />
          </Box>
        )}
        {/* Scrollable content area */}
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 3,
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          {/* Container 1: Create new items */}
          <Card
            variant="outlined"
            ref={composerRef}
            sx={{
              borderColor: "#d2d2d2",
              backgroundColor: "#ffffff",
              overflow: "hidden",
              borderRadius: "12px",
            }}
          >
            <CardContent
              sx={{
                p: 0,
                "&:last-child": {
                  pb: 0,
                },
              }}
            >
              <Box
                sx={{
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", lg: "1.2fr auto" },
                    gap: 1.25,
                    alignItems: "center",
                  }}
                >
                  <FreeSingleSelect
                    options={[]}
                    value={itemCategory}
                    placeholder={t(
                      "meetingNotes.takeNotes.placeholders.category",
                    )}
                    onValueSelected={(value) => setItemCategory(value)}
                    useCategorySuggestions={true}
                    enableUsageSort={true}
                    enableDelete={true}
                    maxVisibleItems={6}
                    hideTooltip={true}
                    sx={{
                      fontSize: "0.875rem",
                      "& .MuiAutocomplete-input": { fontSize: "0.875rem" },
                    }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: { xs: "flex-start", lg: "flex-end" },
                      gap: 0.75,
                      flexWrap: "wrap",
                    }}
                  >
                    {typeOptions.map((option) => {
                      const isSelected = newItemType === option.value;
                      return (
                        <Button
                          key={option.value}
                          size="small"
                          tabIndex={-1}
                          onClick={() => setNewItemType(option.value)}
                          startIcon={option.icon}
                          sx={[
                            {
                              textTransform: "none",
                              borderRadius: "999px",
                              px: 1.5,
                              minWidth: 0,
                              border: "1px solid",

                              "&:hover": {
                                borderColor: "#242C63",
                              },
                            },
                            isSelected
                              ? {
                                  color: "#FFFFFF",
                                }
                              : {
                                  color: "#5C6B85",
                                },
                            isSelected
                              ? {
                                  backgroundColor: "#242C63",
                                }
                              : {
                                  backgroundColor: "#FFFFFF",
                                },
                            isSelected
                              ? {
                                  borderColor: "#242C63",
                                }
                              : {
                                  borderColor: "#d2d2d2",
                                },
                            isSelected
                              ? {
                                  "&:hover": {
                                    backgroundColor: "#1a2048",
                                  },
                                }
                              : {
                                  "&:hover": {
                                    backgroundColor: "#F8FAFC",
                                  },
                                },
                          ]}
                        >
                          {t(option.labelKey)}
                        </Button>
                      );
                    })}
                    <Badge
                      badgeContent={pendingAttachmentsCount}
                      color="primary"
                      sx={{
                        "& .MuiBadge-badge": {
                          fontSize: "0.625rem",
                          height: "16px",
                          minWidth: "16px",
                          padding: "0 4px",
                        },
                      }}
                    >
                      <IconButton
                        size="small"
                        tabIndex={-1}
                        onClick={() => setAttachmentsModalOpen(true)}
                        sx={[
                          {
                            borderRadius: "999px",
                            minWidth: 0,
                            border: "1px solid",

                            "&:hover": {
                              borderColor: "#242C63",
                            },
                          },
                          pendingAttachmentsCount > 0
                            ? {
                                borderColor: "#242C63",
                              }
                            : {
                                borderColor: "#d2d2d2",
                              },
                          pendingAttachmentsCount > 0
                            ? {
                                color: "#242C63",
                              }
                            : {
                                color: "#5C6B85",
                              },
                          pendingAttachmentsCount > 0
                            ? {
                                backgroundColor: "#EEF0FF",
                              }
                            : {
                                backgroundColor: "#FFFFFF",
                              },
                          pendingAttachmentsCount > 0
                            ? {
                                "&:hover": {
                                  backgroundColor: "#E3E7FF",
                                },
                              }
                            : {
                                "&:hover": {
                                  backgroundColor: "#F8FAFC",
                                },
                              },
                        ]}
                      >
                        <AttachFileOutlinedIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Badge>
                  </Box>
                </Box>

                <TextField
                  fullWidth
                  multiline
                  autoFocus
                  minRows={4}
                  maxRows={6}
                  placeholder={t(
                    "meetingNotes.takeNotes.placeholders.description",
                  )}
                  value={itemDescription}
                  onChange={(e) => setItemDescription(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.ctrlKey && e.key === "Enter") {
                      e.preventDefault();
                      void handleSaveItem();
                    }
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#FFFFFF",
                      borderRadius: "8px",
                      fontSize: "0.875rem",
                      "& fieldset": { borderColor: "#d2d2d2" },
                      "&:hover fieldset": { borderColor: "#242C63" },
                      "&.Mui-focused fieldset": { borderColor: "#242C63" },
                    },
                  }}
                />

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: "1.1fr 220px auto auto",
                    },
                    gap: 1.25,
                    alignItems: "center",
                  }}
                >
                  <Tooltip
                    title={
                      newItemType !== MeetingItemType.Task
                        ? t(
                            "meetingNotes.takeNotes.tooltips.responsibleDisabled",
                          )
                        : ""
                    }
                    placement="top"
                    arrow
                  >
                    <Box>
                      <FreeSingleSelect
                        options={[]}
                        value={itemResponsible}
                        placeholder={t(
                          "meetingNotes.takeNotes.placeholders.responsible",
                        )}
                        disabled={newItemType !== MeetingItemType.Task}
                        onValueSelected={(value) => setItemResponsible(value)}
                        useEmailSuggestions={true}
                        pinnedEmails={
                          selectedMeeting?.originalMeeting?.participants
                        }
                        hideTooltip={true}
                        sx={{
                          fontSize: "0.875rem",
                          "& .MuiAutocomplete-input": {
                            fontSize: "0.875rem",
                          },
                        }}
                      />
                    </Box>
                  </Tooltip>
                  <Tooltip
                    title={
                      newItemType !== MeetingItemType.Task
                        ? t("meetingNotes.takeNotes.tooltips.dueDateDisabled")
                        : ""
                    }
                    placement="top"
                    arrow
                  >
                    <Box sx={{ width: "100%" }}>
                      <DatePicker
                        value={itemDueDate}
                        onChange={setItemDueDate}
                        format="DD.MM.YYYY"
                        disabled={newItemType !== MeetingItemType.Task}
                        sx={{ width: "100%" }}
                        slotProps={{
                          textField: {
                            size: "small",
                            fullWidth: true,
                            slotProps: {
                              htmlInput: {
                                placeholder: t(
                                  "meetingNotes.takeNotes.placeholders.dueDate",
                                ),
                              },
                            },
                            sx: {
                              "& .MuiPickersOutlinedInput-root, & .MuiOutlinedInput-root":
                                {
                                  backgroundColor: "#FFFFFF",
                                  fontSize: "0.875rem",
                                  borderRadius: "8px",
                                  height: 38,
                                },
                              "& .MuiPickersOutlinedInput-root.Mui-disabled, & .MuiPickersOutlinedInput-root.Mui-disabled:hover, & .MuiOutlinedInput-root.Mui-disabled, & .MuiOutlinedInput-root.Mui-disabled:hover":
                                {
                                  backgroundColor: "#f5f5f5",
                                },
                              "& .MuiPickersOutlinedInput-input.Mui-disabled, & .MuiOutlinedInput-input.Mui-disabled, & .MuiInputBase-input.Mui-disabled":
                                {
                                  WebkitTextFillColor: "#B0B0B0",
                                  color: "#B0B0B0",
                                },
                              "& .Mui-disabled .MuiSvgIcon-root, & .MuiSvgIcon-root.Mui-disabled":
                                {
                                  color: "#B0B0B0",
                                },
                              "& .MuiPickersOutlinedInput-notchedOutline, & .MuiOutlinedInput-notchedOutline":
                                { borderColor: "#d2d2d2" },
                              "& .MuiPickersOutlinedInput-root:not(.Mui-disabled):hover .MuiPickersOutlinedInput-notchedOutline, & .MuiOutlinedInput-root:not(.Mui-disabled):hover .MuiOutlinedInput-notchedOutline":
                                { borderColor: "#242C63" },
                              "& .MuiPickersOutlinedInput-root.Mui-focused:not(.Mui-disabled) .MuiPickersOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-focused:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline":
                                { borderColor: "#242C63" },
                            },
                          },
                        }}
                      />
                    </Box>
                  </Tooltip>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      justifyContent: { xs: "flex-start", md: "center" },
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={() => guardDirty(resetComposer)}
                      tabIndex={-1}
                      sx={{
                        textTransform: "none",
                        color: "#5C6B85",
                        minWidth: 0,
                      }}
                    >
                      {t("meetingNotes.takeNotes.actions.clear")}
                    </Button>
                    {editingItem && (
                      <Button
                        variant="outlined"
                        onClick={() => void handleCreateNewItem()}
                        disabled={!itemDescription.trim()}
                        sx={{
                          textTransform: "none",
                          color: "#5C6B85",
                          minWidth: 0,
                        }}
                      >
                        {t("meetingNotes.takeNotes.actions.saveAsNew")}
                      </Button>
                    )}
                  </Box>
                  <Button
                    variant="contained"
                    onClick={handleSaveItem}
                    disabled={!itemDescription.trim()}
                    sx={{
                      textTransform: "none",
                      px: 3,
                      minWidth: 140,
                      backgroundColor: "#242C63",
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor: "#353D7A",
                        boxShadow: "none",
                      },
                      "&.Mui-disabled": {
                        backgroundColor: "#AAB0C5",
                        color: "#FFFFFF",
                      },
                    }}
                  >
                    {saveButtonLabel}
                  </Button>
                </Box>
              </Box>

              <Box sx={{ borderTop: "1px solid #C9CED6" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2,
                    py: 1.25,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <LockOutlinedIcon sx={{ fontSize: 16, color: "#5C6B85" }} />
                    <Typography sx={{ fontSize: "0.875rem", color: "#51627F" }}>
                      {t("meetingNotes.takeNotes.personalNotes.title")}
                    </Typography>
                    {pendingPersonalNoteDrafts.length > 0 ? (
                      <Box
                        sx={{
                          px: 0.75,
                          py: 0.25,
                          borderRadius: "999px",
                          backgroundColor: "#EEF0FF",
                          color: "#242C63",
                          fontSize: "0.6875rem",
                          fontWeight: 700,
                          lineHeight: 1.2,
                        }}
                      >
                        {t("meetingNotes.takeNotes.personalNotes.queued", {
                          count: pendingPersonalNoteDrafts.length,
                        })}
                      </Box>
                    ) : null}
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => setPersonalNotesOpen((prev) => !prev)}
                    sx={{ color: "#64748B" }}
                  >
                    {personalNotesOpen ? (
                      <KeyboardArrowUpRoundedIcon />
                    ) : (
                      <KeyboardArrowDownRoundedIcon />
                    )}
                  </IconButton>
                </Box>
                <Collapse in={personalNotesOpen}>
                  <Box sx={{ px: 2, pb: 2 }}>
                    <Typography
                      sx={{
                        mb: 1.25,
                        fontSize: "0.75rem",
                        color: "#7B869C",
                      }}
                    >
                      {personalNotesHelperText}
                    </Typography>
                    <Box sx={{ position: "relative" }}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        placeholder={t(
                          "meetingNotes.takeNotes.personalNotes.draftPlaceholder",
                        )}
                        value={draftPersonalNote}
                        onChange={(e) => setDraftPersonalNote(e.target.value)}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "#FFFFFF",
                            fontSize: "0.875rem",
                            alignItems: "flex-start",
                            "& fieldset": { borderColor: "#d2d2d2" },
                            "&:hover fieldset": { borderColor: "#242C63" },
                            "&.Mui-focused fieldset": {
                              borderColor: "#242C63",
                            },
                          },
                          "& .MuiOutlinedInput-inputMultiline": {
                            pb: 5.5,
                            pr: 6.5,
                          },
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={queueDraftPersonalNote}
                        disabled={!draftPersonalNote.trim()}
                        sx={{
                          position: "absolute",
                          right: 14,
                          bottom: 30,
                          color: "#FFFFFF",
                          backgroundColor: "#242C63",
                          border: "1px solid #D7DCF0",
                          boxShadow: "0 1px 2px rgba(36, 44, 99, 0.08)",
                          "&:hover": { backgroundColor: "#2f386f" },
                          "&.Mui-disabled": {
                            color: "#FFFFFF",
                            backgroundColor: "#AAB0C5",
                          },
                        }}
                      >
                        <AddIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                    {pendingPersonalNoteDrafts.length > 0 ? (
                      <Box
                        sx={{
                          mt: 1.25,
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.75,
                        }}
                      >
                        {pendingPersonalNoteDrafts.map((note, index) => (
                          <Box
                            key={`${note}-${index}`}
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 1,
                              p: 1.25,
                              borderRadius: "10px",
                              backgroundColor: "#FFF8E6",
                              border: "1px solid #F2D48A",
                              overflow: "hidden",
                            }}
                          >
                            <LockOutlinedIcon
                              sx={{ mt: 0.15, fontSize: 14, color: "#A16207" }}
                            />
                            <Box
                              sx={{
                                flex: 1,
                                minWidth: 0,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "0.8125rem",
                                  color: "#1A2048",
                                  whiteSpace: "pre-wrap",
                                  overflowWrap: "anywhere",
                                  wordBreak: "break-word",
                                }}
                              >
                                {note}
                              </Typography>
                              <Typography
                                sx={{
                                  mt: 0.35,
                                  fontSize: "0.6875rem",
                                  color: "#9CA3AF",
                                }}
                              >
                                {t(
                                  "meetingNotes.takeNotes.personalNotes.pending",
                                )}
                              </Typography>
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => removeQueuedPersonalNote(index)}
                              sx={{ color: "#7B869C", p: 0.5 }}
                            >
                              <CloseRoundedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    ) : null}
                  </Box>
                </Collapse>
              </Box>
            </CardContent>
          </Card>

          <MeetingNotesItemGrid
            items={items}
            meetingId={selectedMeeting?.id || ""}
            personalNotes={personalNotes}
            selectedItemId={selectedItemKey}
            onDeleteItem={handleDeleteItem}
            onPersonalNotesChange={onPersonalNotesChange}
            onUpdateAttachmentsCount={handleUpdateAttachmentsCount}
            onUpdateItem={handleUpdateItem}
            onSelectItem={handleSelectItem}
          />

          <AttachmentsModal
            open={attachmentsModalOpen}
            height="600px"
            allowModifications={true}
            meetingId={selectedMeeting?.id}
            itemId={editingItem?.id}
            onClose={handleAttachmentsModalClose}
            onAttachmentsCountChanged={handleAttachmentsCountChanged}
            uploadPrefix={`items/meetings/${selectedMeeting?.id}/items/${editingItem?.id}/`}
            pendingAttachments={
              !editingItem?.id ? pendingAttachments : undefined
            }
            onPendingAttachmentsChange={
              !editingItem?.id ? handlePendingAttachmentsChange : undefined
            }
          />

          <ConfirmationPanel
            showModal={pendingAction !== null}
            hideModal={cancelDiscard}
            confirmModal={confirmDiscard}
            content={t("text.warning.continue_message")}
            confirmButtonText={t("text.warning.continue_without_saving")}
            action="warning"
          />
        </Box>
      </Box>
    );
  },
);

TakeNotesStep.displayName = "TakeNotesStep";

export default TakeNotesStep;
